'use client';

import { compact } from 'es-toolkit';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';
import { toArray } from '@/lib/utils/to-array';

const COLOR_SCHEME_META_SELECTOR = 'meta[name="color-scheme"]';

type Scheme = 'light' | 'dark';

const normalizeScheme = (resolvedTheme?: string | null): Scheme | null => {
  if (resolvedTheme === 'dark') {
    return 'dark';
  }
  if (resolvedTheme === 'light') {
    return 'light';
  }
  return null;
};

const COLOR_SCHEME_TOKEN_PATTERN = /\s+/;

const updateMetaTag = (scheme: Scheme) => {
  const meta = document.querySelector<HTMLMetaElement>(COLOR_SCHEME_META_SELECTOR);
  if (!meta) {
    return;
  }

  const rawContent = meta.getAttribute('content');
  const content = toArray(rawContent ? compact(rawContent.split(COLOR_SCHEME_TOKEN_PATTERN)) : undefined);
  // Keep both tokens for compatibility but ensure the active scheme is first.
  const nextContent = scheme === 'dark' ? 'dark light' : 'light dark';

  if (content.join(' ') !== nextContent) {
    meta.setAttribute('content', nextContent);
  }
};

export function ColorSchemeSync() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const documentReference = globalThis.document ?? null;
    if (!documentReference) {
      return;
    }

    const scheme = normalizeScheme(resolvedTheme);
    if (!scheme) {
      return;
    }

    const root = documentReference.documentElement;
    if (root.style.colorScheme !== scheme) {
      root.style.colorScheme = scheme;
    }

    updateMetaTag(scheme);
  }, [resolvedTheme]);

  return null;
}
